import React from 'react';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Spin } from 'antd';
import Select from 'antd/lib/select';
import cx from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';

import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { IHubspotAccount } from '../../../../slices/accounts/account';
import { selectors as hubspotSelectors } from '../../../../slices/accounts/hubspotAccounts';
import { selectors as accountSelectors } from '../../../../slices/accounts/accountSelectors';
import { fetchHubspotAccounts, IFetchHubspotAccountsRequest } from '../../../../slices/accounts/hubspotAccounts.thunks';
import { hubspotDealsSelector } from '../../../../slices/accounts/hubspotDeals';
import {
  DealTypes,
  fetchHubspotAccountDeals,
  IDeal,
  IFetchDealsRequest
} from '../../../../slices/accounts/hubspotDeals.thunks';
import { RootState } from '../../../../slices/rootReducer';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { AVAILABLE_REGIONS, Regions } from '../../../common/domain/accountRegions';
import { formatNumberAsUSDPrice } from '../../../../utils/utils';
import { IAccountUpgradeToEnterpriseData } from '../../../../slices/accounts/upgrade/interfaces';
import { upgradeAccountToEnterprise } from '../../../../slices/accounts/upgrade/thunk';
import { selectors as UpgradeSelectors } from '../../../../slices/accounts/upgrade/selectors';
import {
  fetchInvoiceDealValidation
} from '../../../../slices/invoices/invoiceDealValidation/invoiceDealValidation.thunks';
import { IInvoiceDealValidationParams } from '../../../../slices/invoices/invoiceDealValidation/types';
import * as dealValidationSelectors
  from '../../../../slices/invoices/invoiceDealValidation/invoiceDealValidation.selectors';
import { ApiError } from '../../../../services/api';
import {
  actions as invoiceDealValidationActions
} from '../../../../slices/invoices/invoiceDealValidation/invoiceDealValidaton.slices';
import { ReactComponent as CrossSVG } from '../../../../assets/sightx-icons/svg/x-close.svg';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { InvoiceDealInstructions } from '../../../common/invoiceDealInstructions/invoiceDealInstructions';
import { SxEmailTags } from '../../../common/SxEmailTags';
import { selectors as accountListSelectors } from '../../../../slices/accounts/list';

import styles from './enterpriseUpgrade.module.scss';

const { clearInvoiceDealValidations } = invoiceDealValidationActions;
interface Props {
  onCancel: () => void;
  accessType: AccessTypes;
  accountId: string;
  hubspotAccountId?: string;
}

interface EnterpriseUpgradeState {
  status: Status;
  hubspotCompanyId?: string;
  serverRegion?: Regions;
  dealId?: string;
  invoiceDetails?: string;
  hubspotSearchTerm: string;
  isUpgrading: boolean;
  // use in Features.BillingUserFacing
  invoicedEmails: string[];
  // end of use in Features.BillingUserFacing

}

enum Status {
  Confirmation,
  CompanySelection,
  ServerSelection,
  DealSelection,
  InvoiceDetails
}

interface StateProps {
  hubspotAccounts: IHubspotAccount[];
  loading: boolean;
  deals: IDeal[];
  dealValidationError?: ApiError;
  loadingDealValidation: boolean;
  dealValidationSuccess: boolean;
  selectedAccountName: string;
  // use in Features.BillingUserFacing
  billingAddressEmail?: string;
  //end of use in Features.BillingUserFacing

}

interface DispatchProps {
  fetchHubspotAccounts: (request: IFetchHubspotAccountsRequest) => void;
  fetchHubspotAccountDeals: (request: IFetchDealsRequest) => void;
  upgradeAccountToEnterprise: (request: IAccountUpgradeToEnterpriseData) => void;
  fetchInvoiceDealValidation: (request: IInvoiceDealValidationParams) => void;
  clearInvoiceDealValidations: () => void;
}

type EnterpriseUpgradeProps = Props & StateProps & DispatchProps;

class EnterpriseUpgradeBase extends React.Component<EnterpriseUpgradeProps, EnterpriseUpgradeState> {
  private firstStep;
  constructor(props: EnterpriseUpgradeProps) {
    super(props);
    const firstStep = props.accessType === AccessTypes.Professional ? Status.CompanySelection : Status.Confirmation;
    this.state = {
      status: firstStep,
      hubspotCompanyId: props.hubspotAccountId,
      hubspotSearchTerm: '',
      serverRegion: Regions.Global,
      isUpgrading: false,
      invoicedEmails: []
    };
    this.firstStep = firstStep;
    this.onNextStep = this.onNextStep.bind(this);
    this.onPreviousStep = this.onPreviousStep.bind(this);
    this.handleHubspotCompanySelect = this.handleHubspotCompanySelect.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.handleHubspotDealSelect = this.handleHubspotDealSelect.bind(this);
    this.changeInvoiceDetails = this.changeInvoiceDetails.bind(this);
    this.handleRegionSelect = this.handleRegionSelect.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleInvoiceEmailChange = this.handleInvoiceEmailChange.bind(this);
  }

  componentDidMount() {
    this.props.fetchHubspotAccounts({ available: true });
    if (this.props.billingAddressEmail) {
      this.setState({ invoicedEmails: [this.props.billingAddressEmail] });
    }
  }

  componentDidUpdate(prevProps: Readonly<EnterpriseUpgradeProps>, prevState: Readonly<EnterpriseUpgradeState>): void {
    const { status, hubspotCompanyId, isUpgrading } = this.state;
    const { loading, dealValidationSuccess } = this.props;
    const { loading: prevLoading } = prevProps;
    if (prevState.status < status && status === Status.DealSelection) {
      const dealTypes = [DealTypes.SubscriptionUpgrade];
      if (isFeatureEnabled(Features.PlgExternal4)) {
        dealTypes.push(DealTypes.Pilot);
      }
      this.props.fetchHubspotAccountDeals({
        filterBy: {
          dealTypes,
          companyHubspotId: hubspotCompanyId,
          invoiced: false
        }
      });
    }

    if (isUpgrading && !loading && prevLoading) {
      this.setState({ isUpgrading: false });
    }

    if (prevState.isUpgrading && !isUpgrading) {
      this.props.onCancel();
    }

    if (status === Status.DealSelection && !prevProps.dealValidationSuccess && dealValidationSuccess) {
      this.setState({ status: Status.InvoiceDetails });
    }

  }

  private handleInvoiceEmailChange(values: string[]) {
    this.setState({ invoicedEmails: values });
  }

  private getTitle(): string {
    const { status } = this.state;
    switch (status) {
    case Status.Confirmation: return 'Upgrade Confirmation';
    case Status.CompanySelection: return 'Upgrade to Enterprise';
    case Status.ServerSelection: return 'Select Server Region';
    case Status.DealSelection: return 'Connect Subscription Upgrade Deal';
    case Status.InvoiceDetails: return 'Invoice Details';
    default: return '';
    }
  }

  private onNextStep() {
    const { accessType, accountId, billingAddressEmail, deals } = this.props;
    const { status, hubspotCompanyId, serverRegion, invoiceDetails, dealId, invoicedEmails } = this.state;

    const recipientEmails = invoicedEmails.filter((email) => email !== billingAddressEmail);
    const carbonCopyRecipients = recipientEmails.length ? recipientEmails : undefined;

    const deal = deals.find((deal) => deal.internalId === dealId);
    const hasDealZeroAmount = isFeatureEnabled(Features.DealZero) && deal?.amount === 0;

    if (status === Status.DealSelection && hasDealZeroAmount && dealId && hubspotCompanyId && serverRegion) {
      this.setState({ isUpgrading: true });
      this.props.upgradeAccountToEnterprise({
        accountId,
        accessType,
        dealId,
        hubspotCompanyId,
        serverRegion,
        invoiceNotes: undefined
      });
      return;
    }
    if (status === Status.DealSelection && dealId && hubspotCompanyId) {
      this.props.fetchInvoiceDealValidation({
        accountIdOrHubspotCompanyId: hubspotCompanyId,
        hubspotDealId: dealId
      });
      return;
    }
    if (status === Status.InvoiceDetails && dealId && hubspotCompanyId && serverRegion) {
      this.setState({ isUpgrading: true });
      this.props.upgradeAccountToEnterprise({
        accountId,
        accessType,
        dealId,
        hubspotCompanyId,
        serverRegion,
        invoiceNotes: invoiceDetails,
        carbonCopyRecipients
      });
      return;
    }
    let increment = 1;
    if (accessType === AccessTypes.Professional && status === Status.CompanySelection) {
      increment = 2;
    }
    this.setState(state => ({ status: state.status + increment }));
  }

  private onPreviousStep() {
    const { accessType, clearInvoiceDealValidations } = this.props;
    const { status } = this.state;
    if (status === this.firstStep) {
      this.props.onCancel();
      return;
    }
    if (status === Status.DealSelection) {
      clearInvoiceDealValidations();
    }
    let decrement = 1;
    if (accessType === AccessTypes.Professional && status === Status.DealSelection) {
      decrement = 2;
    }

    this.setState(state => ({ status: state.status - decrement }));
  }

  private getNextButtonText(): string {
    const { status, dealId } = this.state;
    const { deals } = this.props;
    const deal = deals.find((deal) => deal.internalId === dealId);

    const hasDealZeroAmount = isFeatureEnabled(Features.DealZero) && deal?.amount === 0;

    switch (status) {
    case Status.Confirmation: return 'Continue';
    case Status.InvoiceDetails: return 'Send';
    default: return hasDealZeroAmount ? 'Send' : 'Next';
    }
  }

  private handleHubspotCompanySelect(hubspotCompanyId: string) {
    this.setState({ hubspotCompanyId, hubspotSearchTerm: '', dealId: '' });
  }

  private handleHubspotDealSelect(dealId: string) {
    this.setState({ dealId, hubspotSearchTerm: '' });
    this.props.clearInvoiceDealValidations();
  }

  private handleRegionSelect(serverRegion: Regions) {
    this.setState({ serverRegion });
  }

  private changeInvoiceDetails(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ invoiceDetails: e.target.value });
  }

  private handleCancel() {
    const { clearInvoiceDealValidations, onCancel } = this.props;
    clearInvoiceDealValidations();
    onCancel();
  }

  private renderFooter() {
    const { status, isUpgrading } = this.state;
    return <div className={styles.footer}>
      <Button
        type="default"
        htmlType="button"
        disabled={isUpgrading}
        onClick={this.onPreviousStep}
      >
        {status === this.firstStep ? 'Cancel' : 'Back'}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        onClick={this.onNextStep}
        disabled={this.getIsDisabledNext() || isUpgrading}
      >
        {this.getNextButtonText()}
      </Button>
    </div>;
  }

  private renderWarningMessage(amount: number) {
    if (!isFeatureEnabled(Features.DealZero) || amount > 0) {
      return null;
    }
    return <div className={cx(styles.warning)}>The total amount of this deal is $0, so an invoice will not be sent to the customer.</div>;
  }

  private renderCompanySection() {
    const { hubspotCompanyId } = this.state;
    const { hubspotAccounts, hubspotAccountId, selectedAccountName } = this.props;
    const companyOptions = hubspotAccounts.map(account => ({ key: account.hubspotId, value: account.hubspotId, label: account.name }));
    if (hubspotAccountId) {
      companyOptions.push({ key: hubspotAccountId, value: hubspotAccountId, label: selectedAccountName });
    }
    return <>
      <div className={styles.description}>
        {hubspotAccountId ?
          <>This SightX account is currently connected to the Hubspot company <b>{selectedAccountName}</b>. If the Enterprise deal is associated with a different Hubspot company, select that company in the dropdown below.</> :
          <>This SightX account is not connected to a Hubspot company. Please select a company to connect this account in the dropdown below.</>
        }
      </div>
      <div className={styles.label}>
        Hubspot company
      </div>
      <Select
        value={hubspotCompanyId}
        className={styles.hubspotDropdown}
        placeholder='Search Hubspot companies'
        listHeight={200}
        showSearch
        options={companyOptions}
        filterOption={(input, option) => (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())}
        onChange={this.handleHubspotCompanySelect}
      />
    </>;
  }

  private renderDealSection() {
    const { dealId } = this.state;
    const { deals, dealValidationError } = this.props;
    const deal = deals.find((deal) => deal.internalId === dealId);
    return <>
      <div className={styles.label}>
        Deal name
      </div>
      <Select
        value={dealId}
        className={styles.hubspotDropdown}
        placeholder='Search for deals'
        listHeight={200}
        showSearch
        options={deals.map(deal => ({ key: deal.internalId, value: deal.internalId, label: deal.dealName }))}
        filterOption={(input, option) => (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())}
        onChange={this.handleHubspotDealSelect}
      />
      {
        dealValidationError?.userError ?
          <span className={styles.errorMessage}>
            {dealValidationError.message}. {dealValidationError.url &&
              <a href={dealValidationError.url} target='blank'>Go to deal</a>}
          </span> :
          <FeatureToggle feature={Features.BillingUserFacing}>
            <On>
              <InvoiceDealInstructions />
            </On>
            <Off>
              <div className={styles.additionalInfo}>Deal must be “Closed won” to upgrade the account</div>
            </Off>
          </FeatureToggle>
      }
      {deal ? <>
        <div className={styles.deal}>
          <div><b>Deal type</b> {deal.dealTypeDescription}</div>
          <div><b>Deal close date</b> {moment.utc(deal.closeDate).format('MMMM D, YYYY')}</div>
          <div><b>Contract start date</b> {moment.utc(deal.contractStartDate).format('MMMM D, YYYY')}</div>
        </div>
        <div className={styles.deal}>
          <div><b>Deal Details</b></div>
          {this.renderWarningMessage(deal.amount)}
          {deal.lineItems.map((item, index) => (
            <div className={styles.row} key={item.name}>
              <div>{item.name}</div>
              <div>{item.quantity}</div>
              <div>{formatNumberAsUSDPrice(item.price - (item.discount || 0))}</div>
            </div>
          ))}
          <div className={styles.row}>
            <div>Total</div>
            <div />
            <div>
              {formatNumberAsUSDPrice(deal.amount)}
            </div>
          </div>
        </div>
      </> :
        <div className={cx(styles.deal, styles.centered)}>No deal selected</div>
      }
    </>;
  }

  private renderServerSelection() {
    const { serverRegion } = this.state;
    return <>
      <div className={styles.description}>
        If this account has specified a region for their servers to be located in, select that region below.
      </div>
      <div className={styles.label}>
        Server region
      </div>
      <Select
        value={serverRegion}
        className={styles.hubspotDropdown}
        listHeight={200}
        options={AVAILABLE_REGIONS}
        onChange={this.handleRegionSelect}
      />
      <div className={styles.additionalInfo}>
        Default region is “Global”, with servers located in the US.
      </div>
    </>;
  }

  private onSearchChange(searchTerm: string) {
    this.setState({ hubspotSearchTerm: searchTerm ?? '' });
  }

  private getIsDisabledNext() {
    const { loading, loadingDealValidation } = this.props;
    const { status, hubspotCompanyId, serverRegion, dealId } = this.state;
    switch (status) {
    case Status.CompanySelection:
      return loading || !hubspotCompanyId;
    case Status.ServerSelection:
      return !serverRegion;
    case Status.DealSelection:
      return loadingDealValidation || !dealId;
    default:
      return false;
    }
  }

  private renderLoading() {
    return <div className={styles.loading}>
      <Spin indicator={<LoadingOutlined className={styles.spinner} spin />} />
    </div>;
  }

  private renderEmailInput() {
    return(<>
      <div className={styles.emailsLabel}>Send invoice to</div>
      <div>
        <SxEmailTags
          emails={this.state.invoicedEmails ?? []}
          setEmails={this.handleInvoiceEmailChange}
          maxAllowed={10}
          disabled={this.state.isUpgrading}
          withReadOnly
        />
      </div>
    </>);
  }

  private getContent() {
    const { status, isUpgrading } = this.state;
    const { loading } = this.props;
    switch (status) {
    case Status.Confirmation:
      return <div className={styles.bigText}>
          To complete the upgrade of this Lite account to Enterprise, we'll need to log out all users and close any live campaigns on projects in this account.<br /><br />
          Continue with the upgrade if the above process will not be disruptive to users. Otherwise, exit this flow and plan a time with the customer when the upgrade should occur.
      </div>;
    case Status.CompanySelection:
      if (loading) {
        return this.renderLoading();
      }
      return this.renderCompanySection();
    case Status.ServerSelection:
      return this.renderServerSelection();
    case Status.DealSelection:
      if (loading) {
        return this.renderLoading();
      }
      return this.renderDealSection();
    case Status.InvoiceDetails:
      return <>
        <div className={styles.label}>PO# or invoice notes <i>optional</i></div>
        <Input placeholder="Write here" onChange={this.changeInvoiceDetails} disabled={isUpgrading} />
        {this.renderEmailInput()}
      </>;
    default: return null;
    }
  }

  render() {
    return (
      <Modal
        visible
        className={styles.modal}
        footer={null}
        onCancel={this.handleCancel}
        closeIcon={<Icon component={CrossSVG} />}
      >
        <div className={styles.title}>{this.getTitle()}</div>
        {this.getContent()}
        {this.renderFooter()}
      </Modal>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props): StateProps => {
  const loading = hubspotSelectors.getLoading(state) ||
    hubspotDealsSelector.getLoading(state) ||
    UpgradeSelectors.isSaving(state);
  const billingAddressEmail = accountListSelectors.getAccountById(state, props.accountId)?.billingAddress?.email;

  return {
    hubspotAccounts: hubspotSelectors.getList(state),
    deals: hubspotDealsSelector.getDeals(state),
    loading,
    selectedAccountName: accountSelectors.selectAccountById(props.accountId)(state)?.name ?? '--',
    dealValidationError: dealValidationSelectors.selectInvoiceDealValidationError(state),
    loadingDealValidation: dealValidationSelectors.selectInvoiceDealValidationLoading(state),
    dealValidationSuccess: dealValidationSelectors.selectInvoiceDealValidationCanContinue(state),
    billingAddressEmail
  };
};

const mapDispatchToProps: DispatchProps = {
  fetchHubspotAccounts,
  fetchHubspotAccountDeals,
  upgradeAccountToEnterprise,
  fetchInvoiceDealValidation,
  clearInvoiceDealValidations
};

export const EnterpriseUpgrade = connect(mapStateToProps, mapDispatchToProps)(EnterpriseUpgradeBase);
